import React, { createContext, useContext, useEffect, useState } from 'react';
import seed from './seed';
import { isConfigured } from '../firebase';
import { subscribeOrderedCollection, subscribeDoc } from './firestore';

/*
 * Content provider (SPEC §10). State is seeded from the bundled defaults so the
 * site renders immediately and offline. When Firebase is configured it subscribes
 * to Firestore (realtime) and replaces each slice as data arrives; empty/missing
 * docs fall back to seed so the public site never goes blank.
 */
const ContentContext = createContext(seed);

// Maps Firestore sources → content keys.
const COLLECTIONS = [
  ['services', 'services'],
  ['homeHero', 'homeHero'],
  ['structuralCards', 'structuralCards'],
  ['infrastructureProjects', 'infrastructureProjects'],
  ['researchItems', 'researchItems'],
  ['clients', 'clients'],
  ['popups', 'popups'],
];
const SETTINGS = [
  ['settings/about', 'about'],
  ['settings/homeSections', 'homeSections'],
  ['settings/pageStructural', 'pageStructural'],
  ['settings/pageInfrastructure', 'pageInfrastructure'],
  ['settings/pageResearch', 'pageResearch'],
  ['settings/navigation', 'navigation'],
  ['settings/footer', 'footer'],
  ['settings/contactInfo', 'contactInfo'],
  ['settings/socials', 'socials'],
  ['settings/ctaStructural', 'ctaStructural'],
  ['settings/ctaInfrastructure', 'ctaInfrastructure'],
  ['settings/ctaResearch', 'ctaResearch'],
  ['settings/theme', 'theme'],
  ['settings/seo', 'seo'],
];

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme && theme.colors) {
    Object.entries(theme.colors).forEach(([k, v]) => {
      if (v) root.style.setProperty(`--${k}`, v);
    });
  }
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(() => ({ ...seed, theme: null, seo: null }));

  useEffect(() => {
    if (!isConfigured) return undefined;
    const unsubs = [];

    COLLECTIONS.forEach(([name, key]) => {
      unsubs.push(
        subscribeOrderedCollection(name, (rows) =>
          setContent((c) => ({ ...c, [key]: rows.length ? rows : seed[key] }))
        )
      );
    });

    SETTINGS.forEach(([path, key]) => {
      unsubs.push(
        subscribeDoc(path, (data) =>
          // Merge seed under the doc so fields added to the seed later still appear
          // on already-saved docs (Firestore values win where present).
          setContent((c) => ({ ...c, [key]: data ? { ...(seed[key] || {}), ...data } : seed[key] || null }))
        )
      );
    });

    return () => unsubs.forEach((u) => u && u());
  }, []);

  // Apply theme overrides at runtime (SPEC §12.4).
  useEffect(() => {
    applyTheme(content.theme);
  }, [content.theme]);

  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
}

// ── Hooks (mirror the former data accessors) ───────────────────────────────
export const useContent = () => useContext(ContentContext);
export const useNavigation = () => useContent().navigation;
export const useFooter = () => useContent().footer;
export const useContactInfo = () => useContent().contactInfo;
export const useSocials = () => useContent().socials;
export const useAbout = () => useContent().about;
export const useServices = () => useContent().services;
export const useHomeSections = () => useContent().homeSections;
export const usePageStructural = () => useContent().pageStructural;
export const usePageInfrastructure = () => useContent().pageInfrastructure;
export const usePageResearch = () => useContent().pageResearch;
export const useHomeHero = () => useContent().homeHero;
export const useStructuralCards = () => useContent().structuralCards;
export const useInfraProjects = () => useContent().infrastructureProjects;
export const useResearchItems = () => useContent().researchItems;
export const useClients = () => useContent().clients;
export const usePopups = () => useContent().popups || [];
export const useCtaStructural = () => useContent().ctaStructural;
export const useCtaInfrastructure = () => useContent().ctaInfrastructure;
export const useCtaResearch = () => useContent().ctaResearch;
